/**
 * The Finance dossier for one resident (moved out of FinanceShell in Tide
 * phase 2 so the Finance board can open it under a row). Same sections, same
 * sources, same acts: Record note, Request expedited dispense, and - new here
 * - Send back to UMED on an ACCEPTED handoff, the same signed act the
 * "Handed to Finance" list offers.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  financeApi, outcomeOf, money, shortDate, freshKey, saleTermsLine, NOTE_KIND_LABEL, EXPEDITED_STATE_LABEL,
  FinanceScope, ResidentContext, NoteKind, Urgency, ExpeditedRequest, FinanceDocument, HandoffAmendment, AmendmentTerms, FinanceFlag,
  HANDED_NOT_RECEIVED, correctedWord, centsWords, AwaitingCorrection,
} from './financeApi';
import { Link } from 'react-router-dom';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import HandoffWork from './HandoffWork';
import LedgerBilling, { CloseForm, MondayWarning } from './LedgerBilling';
import { PAID_IN_FULL_OPTIONS } from './paidInFullOptions';
import PaidInFullCorrectionForm, { CORRECTION_NOT_OFFERED, CorrectionStartForm } from './PaidInFullCorrectionForm';
import { PaidInFullForm, PAID_IN_FULL_LABEL } from '../umed/PaidInFullMark';
import AtdPanel from '../../components/atd/AtdPanel';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';
import { APPROVAL_WORD_LABEL, STAGE_LABEL, approvalWordOf, stageLabelOf } from '../umed/umedApi';
import type { Stage } from '../umed/umedApi';

function Fact({ label, value, quiet, source }: { label: string; value: React.ReactNode; quiet?: boolean; source?: string | null }) {
  return (<><dt>{label}</dt><dd className={quiet ? 'none' : ''}>{value}{source ? <span className="fn-source"> · {source}</span> : null}</dd></>);
}

const HANDOFF_STATE_WORD: Record<string, string> = {
  RECEIVED: 'received', ACCEPTED: 'accepted', INVOICED: 'invoiced', TERMS_RECORDED: 'terms recorded', COLLECTING: 'collecting',
  PAID_IN_FULL: 'paid in full', CLOSED: 'closed', RETURNED_TO_UMED: 'returned to UMED',
};
const termsWords = (t: AmendmentTerms | null): string | null => (t && t.months !== null && t.monthlyCents !== null ? `${t.months} × ${money(t.monthlyCents / 100)}` : null);

/**
 * Ruling #1 (Isaiah Cotto, 2026-10-07): a correction or reversal completed after the handoff is an amendment ON the
 * one handoff, never a second one. Each says when (in Florida), what, who, and the terms before and after when known.
 */
function Amendments({ items }: { items: HandoffAmendment[] }) {
  if (!items.length) return null;
  return (
    <div className="fn-amendments" aria-label="Handoff amendments">
      <p>
        <span className="fn-corrected">{correctedWord({ kind: items[0].kind, partial_approval: items[0].partial_approval })} {shortDate(items[0].corrected_on)}</span>
        {' '}{items.length === 1 ? 'One amendment' : `${items.length} amendments`} on the Finance handoff, not a second handoff.<span className="fn-source"> · eMerge</span>
      </p>
      <ul className="fn-notes">
        {items.map((a) => {
          const after = termsWords(a.terms ? a.terms.after : null);
          const before = termsWords(a.terms ? a.terms.before : null);
          return (
            <li key={a.id}>
              <strong>{shortDate(a.corrected_on)}</strong> · {a.summary}{a.amended_by_name ? ` · by ${a.amended_by_name}` : ''}
              {after && <> · terms now {after}{a.terms && a.terms.changed === true && before ? ` (was ${before})` : a.terms && a.terms.changed === false ? ' (unchanged)' : ''}</>}
              <span className="fn-source"> · {a.handoff_state ? `Finance had it ${HANDOFF_STATE_WORD[a.handoff_state] || a.handoff_state.toLowerCase()}` : 'before Finance received it'}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Processes 5 and 6: what UMED told Finance about this resident (a PL correction, Medicaid benefits lost, a re-vet outcome).
 * Information only. It offers no action and never an amount.
 */
function UmedFlags({ items }: { items: FinanceFlag[] }) {
  if (!items.length) return null;
  return (
    <div className="fn-amendments fn-flags" aria-label="UMED flags">
      <p><strong>UMED flags</strong>{' '}{items.length === 1 ? 'One flag' : `${items.length} flags`} from UMED. Information only.<span className="fn-source"> · eMerge</span></p>
      <ul className="fn-notes">
        {items.map((f) => (
          <li key={f.id}>
            <strong>{f.label}</strong> · {shortDate(f.flagged_on)} · {f.summary}{f.flagged_by_name ? ` · by ${f.flagged_by_name}` : ''}
            <span className="fn-source"> · No amount was changed</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NoteForm({ patientId, kinds, onDone }: { patientId: string; kinds: NoteKind[]; onDone: (msg: string) => Promise<void> }) {
  const [kind, setKind] = useState<NoteKind>(kinds[0] || 'NOTE');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <form className="um-form" aria-label="Add a Finance note" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await financeApi.addNote(patientId, { kind, note: note.trim(), idempotency_key: freshKey() });
        if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
        setNote('');
        await onDone(r.data.replayed ? 'That note was already recorded.' : 'Note recorded.');
      } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
    }}>
      <label><span>Kind</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as NoteKind)} aria-label="Note kind">{kinds.map((k) => <option key={k} value={k}>{NOTE_KIND_LABEL[k]}</option>)}</select>
      </label>
      <label><span>Note</span><textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} rows={3} aria-label="Finance note" /></label>
      <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !note.trim()}>{busy ? 'Recording…' : 'Record note'}</button></div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

function ExpediteForm({ patientId, urgencies, onDone }: { patientId: string; urgencies: Urgency[]; onDone: (msg: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState('');
  const [urgency, setUrgency] = useState<Urgency>('ASAP');
  const [byDate, setByDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <form className="um-form" aria-label="Request an expedited dispense" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await financeApi.requestExpedited(patientId, { reason: reason.trim(), arrangement_summary: summary.trim() || undefined, urgency, requested_by_date: urgency === 'REQUESTED_BY_DATE' ? byDate : undefined, idempotency_key: freshKey() });
        if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
        await onDone(r.data.replayed ? 'That request is already with Scheduling.' : 'Request sent to Scheduling. Their answer will appear here.');
      } catch { setErr('Could not reach the server. Nothing was requested.'); } finally { setBusy(false); }
    }}>
      <label><span>Why expedite</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} aria-label="Expedite reason" /></label>
      <label><span>Arrangement (minimum necessary)</span><input value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={1000} aria-label="Arrangement summary" placeholder="e.g. POA offered two payments" /></label>
      <label><span>Urgency</span>
        <select value={urgency} onChange={(e) => setUrgency(e.target.value as Urgency)} aria-label="Expedite urgency">
          {urgencies.map((u) => <option key={u} value={u}>{u === 'ASAP' ? 'As soon as possible' : 'By a date'}</option>)}
        </select>
      </label>
      {urgency === 'REQUESTED_BY_DATE' && <label><span>Requested by</span><input type="date" value={byDate} onChange={(e) => setByDate(e.target.value)} aria-label="Requested by date" /></label>}
      <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !reason.trim()}>{busy ? 'Sending…' : 'Request expedited dispense'}</button></div>
      <p className="um-quiet">Scheduling decides and answers here. This does not change the schedule.</p>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

function ExpeditedCard({ r }: { r: ExpeditedRequest }) {
  return (
    <div className="fn-request" aria-label="Expedited dispense request">
      <strong>{EXPEDITED_STATE_LABEL[r.state]}</strong>
      <span className="um-quiet"> · asked {shortDate(r.requested_at)}{r.requested_by_name ? ` by ${r.requested_by_name}` : ''}{r.urgency === 'REQUESTED_BY_DATE' && r.requested_by_date ? ` · by ${shortDate(r.requested_by_date)}` : ''}</span>
      <p>{r.reason}</p>
      {r.scheduling_response && <p className="um-quiet">Scheduling{r.scheduling_owner_name ? ` (${r.scheduling_owner_name})` : ''}: {r.scheduling_response}</p>}
      {r.scheduled_for && <p>Visit {shortDate(r.scheduled_for)}{r.visit_ref ? ` · ${r.visit_ref}` : ''}</p>}
    </div>
  );
}

function DocRow({ patientId, doc }: { patientId: string; doc: FinanceDocument }) {
  const [err, setErr] = useState<string | null>(null);
  const open = async () => {
    setErr(null);
    try {
      const r = await financeApi.documentUrl(patientId, doc.id);
      if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch { setErr('Could not reach the server.'); }
  };
  const label = doc.document_class === 'BOM_VETTING' ? 'BOM vetting' : doc.document_class === 'RECORDING' ? 'Recording' : doc.document_class.replace(/_/g, ' ');
  return (
    <li><span>{label}{doc.original_filename ? ` · ${doc.original_filename}` : ''} · {shortDate(doc.source_created_at || doc.imported_at || doc.created_at)}</span>{' '}
      <button type="button" className="um-link" onClick={open}>Open</button>{err && <span className="um-notice stop" role="alert"> {err}</span>}</li>
  );
}

/**
 * CORRECTIONS R2 (Josie Evans, 2026-10-28): the approval Finance has, and the months it cannot collect yet. Each term month
 * reads its collectible amount or "Correction", then the total collectible now (director's ruling 2); the correction's
 * state says whether the corrected UMED was sent back to DCF. Terms with no start date list only the months awaiting.
 * Read only: it changes no Finance state.
 */
export function CorrectionMonths({ a }: { a: AwaitingCorrection }) {
  const s = a.schedule || null;
  const sent = a.state_words ? `${a.state_words}${a.sent && a.sent_on ? ` ${shortDate(a.sent_on)}` : ''}` : null;
  return (
    <div className="fn-amendments" aria-label="Months awaiting a correction" data-testid="correction-months">
      <p>
        <strong>{a.word}</strong>{sent ? <> · <span data-testid="correction-state">{sent}</span></> : null}
        {a.since ? <span className="um-quiet"> · since {shortDate(a.since)}</span> : null}<span className="fn-source"> · eMerge</span>
      </p>
      {a.no_months_words && <p data-testid="correction-no-months">{a.no_months_words}. No month was named, so eMerge cannot say which months are affected.</p>}
      {s && s.months.length > 0 && (
        <ul className="fn-notes" aria-label="Per month">
          {s.months.map((m) => (
            <li key={m.month} data-testid={`correction-month-${m.month}`}>
              <strong>{m.label}</strong>{' '}
              {m.state === 'CORRECTION'
                ? <>Correction{m.cents !== null ? <span className="um-quiet"> · {centsWords(m.cents)} not collectible until it is corrected</span> : null}{m.outside_terms ? <span className="um-quiet"> · outside the recorded terms</span> : null}</>
                : <>{centsWords(m.cents)} collectible</>}
            </li>
          ))}
        </ul>
      )}
      {s && s.dated && s.collectible_cents !== null && (
        <p data-testid="correction-collectible">Collectible now: <strong>{centsWords(s.collectible_cents)}</strong>
          {s.term_months !== null && s.monthly_cents !== null ? <span className="um-quiet"> · terms {s.term_months} × {centsWords(s.monthly_cents)}</span> : null}</p>
      )}
      {s && !s.dated && <p className="um-quiet" data-testid="correction-undated">The terms have no start date in eMerge, so only the months awaiting the correction are listed.</p>}
    </div>
  );
}

/**
 * The Corrections section: what is open (the months, and R5's read-only "Paid in full - needs correction"), and the act
 * that starts one of the two types Finance may start (R3). Offered only with no correction and no DCF resubmittal open.
 */
function CorrectionsSection({ ctx, scope, onChanged }: { ctx: ResidentContext; scope: FinanceScope; onChanged: (msg: string) => Promise<void> }) {
  const [starting, setStarting] = useState(false);
  const canMark = Boolean(ctx.umed) && scope.writes === 'NATIVE' && Boolean(scope.can?.manage_handoff) && ctx.umed!.stage !== 'CLOSED';
  const whyNot = ctx.umed ? CORRECTION_NOT_OFFERED(ctx.umed) : null;
  const canStart = canMark && !whyNot;
  const a = ctx.awaiting_correction || null;
  const pif = ctx.paid_in_full_correction || null;
  if (!ctx.umed && !a && !pif) return null;
  return (
    <div className="um-section" role="group" aria-label="Corrections" data-testid="finance-corrections">
      <h3>Corrections</h3>
      {pif && (
        <p data-testid="pif-needs-correction">
          <strong>{pif.words}</strong> · {pif.state_words}{pif.sent && pif.sent_on ? ` ${shortDate(pif.sent_on)}` : ''}
          {pif.since ? <span className="um-quiet"> · requested {shortDate(pif.since)}</span> : null}
          <span className="fn-source"> · read only; the Finance board does not change</span>
        </p>
      )}
      {a && <CorrectionMonths a={a} />}
      {!a && !pif && <p className="um-quiet">No correction is open for this resident.</p>}
      {ctx.umed && canStart && !starting && (
        <button type="button" className="um-open" data-testid="correction-start-open" onClick={() => setStarting(true)}>Start a correction…</button>
      )}
      {ctx.umed && canStart && starting && (
        <CorrectionStartForm patientId={ctx.resident.id} caseId={ctx.umed.case_id} name={ctx.resident.name}
          onClose={() => setStarting(false)} onDone={(m) => { setStarting(false); void onChanged(m); }} />
      )}
      {ctx.umed && !canStart && (
        <p className="um-quiet" data-testid="correction-start-why-not">{whyNot || 'You do not have permission to start a correction from Finance.'}</p>
      )}
    </div>
  );
}

/**
 * `handoffActs` false (the Finance Kanban's dock, 2026-10-14): the handoff work and the send-back are drawn by the
 * card's own act panel above, against the card's handoff, so the dossier shows the resident for context only.
 * `atd` false (2026-10-15): the card's act panel draws the ATD panel itself, for the CARD's resident and near the top,
 * so the dossier beneath does not draw a second one. `onAtdChanged`: ATD was changed in the panel (which re-reads
 * itself and says what happened); the board around it re-reads its rows so the ATD column is current.
 */
/** Finance marks or clears "Paid in full, UMED pending" on the resident's UMED case (FINANCE_HANDOFF_MANAGE, 2026-10-15). */
function PaidInFullAct({ patientId, caseId, name, marked, onDone }: { patientId: string; caseId: string; name: string; marked: boolean; onDone: (msg: string) => Promise<void> | void }) {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <PaidInFullForm mode={marked ? 'clear' : 'mark'} name={name} onClose={() => setOpen(false)} onDone={(m) => { void onDone(m); }}
        act={(body) => (marked
          ? financeApi.clearPaidInFull(patientId, caseId, { reason: body.reason, idempotency_key: body.idempotency_key })
          : financeApi.markPaidInFull(patientId, caseId, body))} />
    );
  }
  return (
    <button type="button" className="um-open" data-testid="finance-pif-open" onClick={() => setOpen(true)}>
      {marked ? 'Clear paid in full…' : 'Mark paid in full (UMED pending)…'}
    </button>
  );
}

/**
 * One "Paid in full" group (2026-10-20): close with Finance, flag UMED pending, or start a Paid in full correction (2026-10-21:
 * Finance starts it here, under FINANCE_HANDOFF_MANAGE; before that it only linked to the UMED board).
 */
function PaidInFullGroup({ ctx, scope, onChanged }: { ctx: ResidentContext; scope: FinanceScope; onChanged: (msg: string) => Promise<void> }) {
  const [closing, setClosing] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const l = ctx.ledger;
  const canPay = scope.writes === 'NATIVE' && Boolean(scope.can?.record_payments);
  const canClose = canPay && Boolean(l && l.available);
  const closeWhy = scope.writes !== 'NATIVE' || !scope.can?.record_payments ? 'You do not have permission to record payments.' : 'The eMerge ledger is not available for this resident.';
  const canMark = Boolean(ctx.umed) && scope.writes === 'NATIVE' && Boolean(scope.can?.manage_handoff) && ctx.umed!.stage !== 'CLOSED';
  // A new correction is offered only while no correction and no DCF resubmittal is open on the case (the UMED board's own rule).
  const whyNotCorrect = ctx.umed ? CORRECTION_NOT_OFFERED(ctx.umed) : null;
  const canCorrect = canMark && !whyNotCorrect;
  const correctionWhy = whyNotCorrect || (canMark ? null : 'You do not have permission to start a correction from Finance.');
  const umedCaseLink = ctx.umed ? `/portal/umed?view=work&case=${encodeURIComponent(ctx.umed.case_id)}` : '';
  if (!canPay && !canMark) return null;
  return (
    <div className="um-section" role="group" aria-label="Paid in full" data-testid="finance-pif-group">
      <h3>Paid in full</h3>
      {PAID_IN_FULL_OPTIONS.map((o) => (
        <div key={o.key} data-testid={`pif-option-${o.key}`}>
          {o.key === 'close' && (
            <button type="button" className="um-open" disabled={!canClose || closing} onClick={() => setClosing(true)}>{o.label}</button>
          )}
          {o.key === 'umed_pending' && canMark && ctx.umed && (
            <PaidInFullAct patientId={ctx.resident.id} caseId={ctx.umed.case_id} name={ctx.resident.name} marked={ctx.umed.paid_in_full?.state === 'MARKED'} onDone={onChanged} />
          )}
          {o.key === 'correction' && ctx.umed && canCorrect && !correcting && (
            <button type="button" className="um-open" data-testid="pif-correction-open" onClick={() => setCorrecting(true)}>{`${o.label}…`}</button>
          )}
          {o.key === 'correction' && ctx.umed && canCorrect && correcting && (
            <PaidInFullCorrectionForm patientId={ctx.resident.id} caseId={ctx.umed.case_id} name={ctx.resident.name} effect={o.effect}
              onClose={() => setCorrecting(false)} onDone={(m) => { setCorrecting(false); void onChanged(m); }} />
          )}
          {(o.key === 'close' || (o.key === 'umed_pending' && canMark) || (o.key === 'correction' && ctx.umed && !(canCorrect && correcting))) && <p className="um-quiet">{o.effect}</p>}
          {o.key === 'correction' && ctx.umed && (
            <p className="um-quiet">
              {correctionWhy && <><span data-testid="pif-correction-why-not">{correctionWhy}</span>{' '}</>}
              <Link className={canCorrect ? 'um-link' : 'um-open'} data-testid="pif-correction-link" aria-label={`Open the UMED case for ${ctx.resident.name}`} to={umedCaseLink}>Open the UMED case</Link>
            </p>
          )}
          {o.key === 'close' && !canClose && <p className="um-quiet">{closeWhy}</p>}
        </div>
      ))}
      {closing && canClose && l && l.available && (
        <CloseForm ctx={ctx} scope={scope} ledger={l} onClose={() => setClosing(false)} onDone={async (m) => { setClosing(false); await onChanged(m); }} />
      )}
    </div>
  );
}

export function Context({ ctx, scope, notice, onChanged, onReload, handoffActs = true, atd = true, onAtdChanged }: {
  ctx: ResidentContext; scope: FinanceScope; notice: string | null; onChanged: (msg: string) => Promise<void>; onReload?: () => Promise<void>;
  handoffActs?: boolean; atd?: boolean; onAtdChanged?: (msg: string) => void;
}) {
  const a = ctx.account;
  const rp = ctx.responsible_party;
  const open = ctx.expedited_dispense.open;
  // Next visit (authority ruling 2026-10-05): eMerge's Scheduling plan; Monday's row is legacy reference only.
  const nv = ctx.next_visit;
  const nvMonday = nv.monday !== undefined
    ? nv.monday
    : (nv.projected || nv.tentative || nv.last_visit ? { projected: nv.projected, tentative: nv.tentative, last_visit: nv.last_visit } : null);
  return (
    <section className="um-card" aria-label="Resident context">
      <div className="um-card-head">
        <div>
          <h2>{ctx.resident.name}</h2>
          <p className="why">{ctx.facility ? ctx.facility.name : <BuildingOrClinic row={{ counselear_clinic_id: ctx.counselear_clinic?.id ?? null, counselear_clinic_name: ctx.counselear_clinic?.name ?? null }} fallback="No current building" />}{ctx.resident.room ? ` · Room ${ctx.resident.room}` : ''}{ctx.resident.census_status ? ` · ${ctx.resident.census_status}` : ''}</p>
        </div>
      </div>
      {notice && <p className="um-notice ok" role="status">{notice}</p>}

      <div className="um-section">
        <h3>Current Finance work</h3>
        {open ? <ExpeditedCard r={open} /> : <p className="um-quiet">No expedited dispense request is open.</p>}
        {ctx.umed && ctx.umed.finance_state !== 'NOT_OPEN' && <p>UMED Finance state: <strong>{ctx.umed.finance_state.replace(/_/g, ' ').toLowerCase()}</strong></p>}
        {ctx.handoff && (
          <p>
            {ctx.handoff.kind === 'PRIVATE_PAY' ? 'Finance handoff (Private pay, no UMED)' : 'Finance handoff'}: <strong>{ctx.handoff.state.replace(/_/g, ' ').toLowerCase()}</strong><span className="fn-source"> · eMerge</span>
            {ctx.handoff.kind === 'PRIVATE_PAY' && saleTermsLine(ctx.handoff.sale) && <><br /><span className="fn-pp-terms">Sale: {saleTermsLine(ctx.handoff.sale)}</span></>}
          </p>
        )}
        {/* 2026-10-20: UMED handed the resident over and Finance has not received it -- eMerge's word, not the Monday mirror's. */}
        {ctx.handed_not_received && (
          <p data-testid="handed-not-received">Finance handoff: <strong>{HANDED_NOT_RECEIVED}</strong><span className="fn-source"> · eMerge</span></p>
        )}
        {ctx.amendments && <Amendments items={ctx.amendments} />}
        {ctx.flags && <UmedFlags items={ctx.flags} />}
        {/* Only the person who accepted a handoff holds it, so only an ACCEPTED one is offered; the preview and the server decide again. */}
        {handoffActs && scope.can?.send_back && ctx.handoff && ctx.handoff.kind !== 'PRIVATE_PAY' && ctx.handoff.state === 'ACCEPTED' && (
          <div className="fn-sendback">
            <SendBackAction path={sendBackPath.finance(ctx.handoff.id)} recordLabel={ctx.resident.name} className="um-open" label="Send back to UMED…"
              onClosed={(done) => { if (done) onChanged('Sent back to UMED.'); }} />
          </div>
        )}
      </div>

      {/* The handoff work screens (2026-10-10): Finance's own record and the acts the caller may take now; gated by scope.can. */}
      {handoffActs && <HandoffWork key={ctx.handoff?.id ?? ctx.resident.id} ctx={ctx} scope={scope} onChanged={onChanged} onReload={onReload || (() => onChanged(''))} />}

      {atd && <AtdPanel patientId={ctx.resident.id} onChanged={onAtdChanged} />}

      <LedgerBilling ctx={ctx} scope={scope} onChanged={onChanged} />

      <div className="um-section">
        <h3>Monday historical mirror (not the live balance)</h3>
        {a.historical && <MondayWarning text={a.historical.warning} />}
        <dl className="um-kv">
          <Fact label="Account status" value={a.status || (ctx.handed_not_received ? HANDED_NOT_RECEIVED : 'No Finance account')} quiet={!a.status && !ctx.handed_not_received} source={a.status || !ctx.handed_not_received ? a.source : 'eMerge'} />
          <Fact label="Sale value" value={money(a.total_sale_value)} />
          <Fact label="Payments received" value={money(a.payments_received)} />
          <Fact label="Remaining" value={money(a.remaining_amount)} />
          <Fact label="Overdue" value={money(a.amount_overdue)} />
          <Fact label="Terms" value={a.terms || '—'} quiet={!a.terms} />
          <Fact label="Aging" value={a.aging.state === 'DERIVED'
            ? `current ${money(a.aging.current)} · 1–30 ${money(a.aging.d1_30)} · 31–60 ${money(a.aging.d31_60)} · 61–90 ${money(a.aging.d61_90)} · 90+ ${money(a.aging.d90_plus)}`
            : a.aging.why || 'Not available'} quiet={a.aging.state !== 'DERIVED'} />
        </dl>
      </div>

      <div className="um-section">
        <h3>Who pays, who decides</h3>
        <dl className="um-kv">
          <Fact label="Financial responsibility" value={rp.financial.type ? rp.financial.type.replace(/_/g, ' ').toLowerCase() : 'Not recorded'} quiet={!rp.financial.type} source={rp.financial.source} />
          <Fact label="Responsible party" value={rp.financial.name ? `${rp.financial.name}${rp.financial.phone ? ` · ${rp.financial.phone}` : ''}${rp.financial.email ? ` · ${rp.financial.email}` : ''}` : 'Not recorded'} quiet={!rp.financial.name} />
          <Fact label="Decision maker (POA)" value={rp.decision_maker.name ? `${rp.decision_maker.name}${rp.decision_maker.phone ? ` · ${rp.decision_maker.phone}` : ''}` : 'Not recorded'} quiet={!rp.decision_maker.name} source={rp.decision_maker.source} />
          <Fact label="Patient liability" value={ctx.liability.value === null ? 'Not recorded' : money(ctx.liability.value)} quiet={ctx.liability.value === null}
            source={ctx.liability.source === 'PA_ENTERED' ? `PA-entered${ctx.liability.checked_at ? `, checked ${shortDate(ctx.liability.checked_at)}` : ''}` : null} />
          {ctx.liability.conflict && <Fact label="Reconciliation" value="Needs reconciliation — a prior source carries a different amount." />}
        </dl>
      </div>

      <div className="um-section">
        <h3>Approval and UMED</h3>
        {ctx.umed ? (
          <dl className="um-kv">
            <Fact label="Enrollment" value={ctx.enrollment.state === 'ENROLLED' ? 'Enrolled' : ctx.enrollment.state === 'LEAD' ? `Lead · ${ctx.enrollment.lead_status || 'status not recorded'}` : 'No lead'} source={ctx.enrollment.source} />
            {/* Approved is the act (final verification 2026-10-06, P2-C): FLMMIS is its evidence, Monday's stage is Monday's claim. */}
            <Fact label="UMED stage" value={STAGE_LABEL[ctx.umed.stage as Stage] ? stageLabelOf(ctx.umed.stage as Stage, ctx.umed.approval.proven) : ctx.umed.stage.replace(/_/g, ' ').toLowerCase()} />
            <Fact label="Approval" value={APPROVAL_WORD_LABEL[approvalWordOf({ proven: ctx.umed.approval.proven, flmmis: ctx.umed.flmmis, stage: ctx.umed.stage, finance_state: ctx.umed.finance_state })]}
              quiet={!ctx.umed.approval.proven} source={ctx.umed.approval.proven && ctx.umed.approved_on ? shortDate(ctx.umed.approved_on) : null} />
            <Fact label="NOCA / FLMMIS" value={`${ctx.umed.noca} / ${ctx.umed.flmmis}`} />
            <Fact label="Sent to DCF" value={ctx.umed.last_native_sending ? `${shortDate(ctx.umed.last_native_sending)} (recorded in eMerge)` : ctx.umed.sent_to_dcf_on || ctx.umed.submitted_on ? `${shortDate(ctx.umed.sent_to_dcf_on || ctx.umed.submitted_on)} (board date)` : 'Not sent'} />
            {/* Gwynn, 2026-10-12: "DCF Email Created" (the account made) and "(STUCK) Can't link benefits", as the UMED side recorded them. */}
            {ctx.umed.dcf && (
              <Fact label="DCF account"
                value={ctx.umed.dcf.account && ctx.umed.dcf.account.state === 'ACCOUNT_CREATED'
                  ? `DCF email created${ctx.umed.dcf.account.recorded_by_name ? ` by ${ctx.umed.dcf.account.recorded_by_name}` : ''}${ctx.umed.dcf.account.recorded_at ? ` ${shortDate(ctx.umed.dcf.account.recorded_at)}` : ''}`
                  : ctx.umed.dcf.account ? ctx.umed.dcf.account.state.replace(/_/g, ' ').toLowerCase() : 'Not recorded in eMerge'}
                quiet={!ctx.umed.dcf.account || ctx.umed.dcf.account.state !== 'ACCOUNT_CREATED'} source={ctx.umed.dcf.account ? 'UMED' : null} />
            )}
            {ctx.umed.dcf && ctx.umed.dcf.benefits && (
              <Fact label="DCF benefits"
                value={ctx.umed.dcf.benefits.state === 'STUCK'
                  ? `(STUCK) Can’t link benefits${ctx.umed.dcf.benefits.reason ? `: ${ctx.umed.dcf.benefits.reason}` : ''}`
                  : `Linked${ctx.umed.dcf.benefits.note ? `: ${ctx.umed.dcf.benefits.note}` : ''}`}
                source={[ctx.umed.dcf.benefits.recorded_by_name, ctx.umed.dcf.benefits.recorded_at ? shortDate(ctx.umed.dcf.benefits.recorded_at) : null].filter(Boolean).join(' ') || null} />
            )}
            {ctx.advocate && <Fact label="Patient advocate" value={ctx.advocate.name || 'Unassigned'} quiet={!ctx.advocate.name} />}
            {/* "Paid in full, UMED pending" (Isaiah Cotto, 2026-10-15): the UMED goes on; once approved, reimbursement is owed. */}
            {ctx.umed.paid_in_full && (
              <Fact label="Paid in full"
                value={ctx.umed.paid_in_full.state === 'MARKED'
                  ? `${PAID_IN_FULL_LABEL}${ctx.umed.paid_in_full.paid_on ? ` · paid ${shortDate(ctx.umed.paid_in_full.paid_on)}` : ''}${ctx.umed.paid_in_full.reason ? `: ${ctx.umed.paid_in_full.reason}` : ''}`
                  : `Cleared${ctx.umed.paid_in_full.reason ? `: ${ctx.umed.paid_in_full.reason}` : ''}`}
                quiet={ctx.umed.paid_in_full.state !== 'MARKED'}
                source={[ctx.umed.paid_in_full.recorded_by_name, ctx.umed.paid_in_full.recorded_at ? shortDate(ctx.umed.paid_in_full.recorded_at) : null].filter(Boolean).join(' ') || null} />
            )}
            {ctx.umed.reimbursement_note && <Fact label="Reimbursement" value={ctx.umed.reimbursement_note} />}
          </dl>
        ) : <p className="um-quiet">No UMED case for this resident.</p>}
      </div>

      <CorrectionsSection ctx={ctx} scope={scope} onChanged={onChanged} />

      <PaidInFullGroup ctx={ctx} scope={scope} onChanged={onChanged} />

      <div className="um-section">
        <h3>Provider and next visit</h3>
        <dl className="um-kv">
          <Fact label="Last tested" value={ctx.testing.state === 'SOURCE_ASSERTED' ? `${shortDate(ctx.testing.last_tested)}${ctx.testing.tested_by ? ` by ${ctx.testing.tested_by}` : ''} (CounselEAR-reported)` : 'Not available'} quiet={ctx.testing.state !== 'SOURCE_ASSERTED'} />
          {ctx.testing.state !== 'SOURCE_ASSERTED' && <Fact label="" value={<span className="um-quiet">{ctx.testing.why}</span>} />}
          <Fact label="Next visit" value={nv.native && nv.native.on ? `${shortDate(nv.native.on)} (${nv.native.state === 'CONFIRMED' ? 'confirmed' : 'planned'}${nv.native.provider_name ? `, ${nv.native.provider_name}` : ''})` : 'No visit planned in eMerge'} quiet={!nv.native} source="eMerge Scheduling" />
          <Fact label="Last visit Scheduling completed" value={nv.last_completed ? shortDate(nv.last_completed.on) : 'None recorded in eMerge'} quiet={!nv.last_completed} source={nv.last_completed ? 'eMerge Scheduling' : null} />
          {nvMonday && (
            <Fact
              label="Monday (legacy reference, read-only)"
              value={`next ${nvMonday.projected ? shortDate(nvMonday.projected) : nvMonday.tentative ? `${shortDate(nvMonday.tentative)} (tentative)` : 'not recorded'} · last ${nvMonday.last_visit ? shortDate(nvMonday.last_visit) : 'not recorded'}`}
              quiet
            />
          )}
        </dl>
        {ctx.facility && ctx.facility.contacts.length > 0 && (
          <ul className="fn-contacts" aria-label="Facility contacts">
            {ctx.facility.contacts.slice(0, 5).map((c, i) => <li key={i}>{c.name}{c.title ? ` · ${c.title}` : ''}{c.phone ? ` · ${c.phone}` : ''}{c.email ? ` · ${c.email}` : ''}</li>)}
          </ul>
        )}
      </div>

      <div className="um-section">
        <h3>Documents and recordings</h3>
        {!ctx.documents.available ? <p className="um-quiet">{ctx.documents.why}</p>
          : (ctx.documents.items || []).length === 0 ? <p className="um-quiet">eMerge holds no document for this resident.</p>
            : <ul className="um-packets" aria-label="Resident documents">{(ctx.documents.items || []).map((d) => <DocRow key={d.id} patientId={ctx.resident.id} doc={d} />)}</ul>}
      </div>

      <div className="um-section">
        <h3>Recent history</h3>
        {ctx.notes.length > 0 && <ul className="fn-notes" aria-label="Finance notes">{ctx.notes.slice(0, 10).map((n) => <li key={n.id}><span className="um-quiet">{shortDate(n.recorded_at)} · {NOTE_KIND_LABEL[n.kind]}{n.recorded_by_name ? ` · ${n.recorded_by_name}` : ''}</span><br />{n.note}</li>)}</ul>}
        {ctx.history.length === 0 ? <p className="um-quiet">Nothing recorded yet.</p>
          : <ol className="um-story">{ctx.history.slice(0, 12).map((h, i) => <li key={i}><span className="when">{shortDate(h.at)}</span><span className="what">{h.what.replace(/_/g, ' ').toLowerCase()}{h.who ? ` · ${h.who}` : ''}{h.source === 'MONDAY' ? ' · from the board' : ''}</span></li>)}</ol>}
        {ctx.expedited_dispense.history.filter((r) => r.id !== open?.id).map((r) => <ExpeditedCard key={r.id} r={r} />)}
      </div>

      {scope.writes === 'NATIVE' && (scope.can.add_note || scope.can.request_expedited_dispense) && (
        <div className="um-section">
          <h3>Actions</h3>
          {scope.can.add_note && <NoteForm patientId={ctx.resident.id} kinds={scope.note_kinds} onDone={onChanged} />}
          {scope.can.request_expedited_dispense && !open && <ExpediteForm patientId={ctx.resident.id} urgencies={scope.urgencies} onDone={onChanged} />}
        </div>
      )}
      {scope.writes === 'NONE' && <div className="um-readonly"><strong>Read-only.</strong> Finance notes and expedited-dispense requests are not open on this process yet.</div>}
    </section>
  );
}

/**
 * Load one resident's dossier and render it. A failure is said, never shown
 * as an empty dossier; a resident outside the caller's scope is "not found".
 */
export default function ResidentDossier({ patientId, scope, onChanged, onAtdChanged }: { patientId: string; scope: FinanceScope; onChanged?: (msg: string) => void; onAtdChanged?: (msg: string) => void }) {
  const [ctx, setCtx] = useState<ResidentContext | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The resident this dossier is for, so a late response for another resident is dropped.
  const current = useRef(patientId);
  current.current = patientId;
  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await financeApi.resident(patientId);
      if (current.current !== patientId) return;
      if (!r.success || !r.data) { setCtx(null); setErr(r.code === 'NOT_FOUND' ? 'No such resident here. They may be in a building outside your scope.' : outcomeOf(r)); return; }
      setCtx(r.data);
    } catch { if (current.current === patientId) { setCtx(null); setErr('Could not reach the server.'); } }
  }, [patientId]);
  useEffect(() => { setNotice(null); setCtx(null); load(); }, [load]);
  if (err) return <section className="um-card"><p className="um-notice stop" role="alert">{err} <button type="button" className="um-open" onClick={load}>Try again</button></p></section>;
  if (!ctx) return <section className="um-card"><p className="um-quiet" role="status">Loading the resident's Finance context…</p></section>;
  return <Context ctx={ctx} scope={scope} notice={notice} onAtdChanged={onAtdChanged} onReload={async () => { setNotice(null); await load(); }} onChanged={async (m) => { setNotice(m); if (onChanged) onChanged(m); await load(); }} />;
}
